import { useEffect, useState, type ReactNode } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import { formatClockTimesInText, useTimeFormat } from '../../lib/timeFormat'
import { getAdminOverview, whenLabel, type ActivityKind, type AdminOverview } from './adminOverviewStore'

/**
 * The administrator's Overview: counts of accounts, teams, and employees, and
 * the latest administrative events, each read from the database when the page
 * opens (adminOverviewStore.ts).
 */
function AccountsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  )
}

function OfficesIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h6" />
    </svg>
  )
}

function ShiftTemplateIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

function AccountDeactivatedIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
      <path d="M18 8l3 3M21 8l-3 3" />
    </svg>
  )
}

const ACTIVITY_META: Record<ActivityKind, { icon: ReactNode; iconClass: string }> = {
  account: { icon: <AccountsIcon />, iconClass: 'bg-selected text-primary' },
  role: { icon: <AccountsIcon />, iconClass: 'bg-selected text-primary' },
  deactivated: { icon: <AccountDeactivatedIcon />, iconClass: 'bg-error/10 text-error' },
  organization: { icon: <OfficesIcon />, iconClass: 'bg-surface-2 text-ink-muted' },
  schedule: { icon: <ShiftTemplateIcon />, iconClass: 'bg-surface-2 text-ink-muted' },
}

export default function AdminDashboardPage() {
  const { format } = useTimeFormat()
  const [overview, setOverview] = useState<AdminOverview | null>(null)

  useEffect(() => {
    let cancelled = false
    getAdminOverview().then((loaded) => {
      if (!cancelled) setOverview(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const tiles: { label: string; value: number }[] = overview
    ? [
        { label: 'Active accounts', value: overview.activeAccounts },
        { label: 'Deactivated accounts', value: overview.deactivatedAccounts },
        { label: 'Open teams', value: overview.openTeams },
        { label: 'Employees', value: overview.employees },
      ]
    : []

  return (
    <AdminLayout current="overview" title="Admin Overview">
      {!overview ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {tiles.map((tile) => (
              <div key={tile.label} className="rounded-surface border border-line bg-canvas p-4">
                <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{tile.label}</p>
                <p className="mt-1 text-h1 font-extrabold [font-variant-numeric:tabular-nums]">{tile.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-surface border border-line bg-canvas">
            <div className="border-b border-line px-4 py-3">
              <h2 className="text-body font-extrabold">Recent activity</h2>
            </div>
            {overview.activity.length === 0 ? (
              <EmptyState>No administrative activity yet.</EmptyState>
            ) : (
              <ul>
                {overview.activity.map((entry) => {
                  const meta = ACTIVITY_META[entry.kind]
                  return (
                    <li key={entry.id} className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
                      <span className={`flex size-8 flex-none items-center justify-center rounded-full ${meta.iconClass}`}>
                        {meta.icon}
                      </span>
                      <div className="flex-1">
                        <p className="text-small font-bold">{formatClockTimesInText(entry.title, format)}</p>
                        <p className="mt-0.5 text-caption text-ink-muted">{formatClockTimesInText(entry.detail, format)}</p>
                      </div>
                      <span className="flex-none text-caption text-ink-subtle">{whenLabel(entry.occurredAt)}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  )
}
